/**
 * FBS Rating Themes
 *
 * All available rating theme definitions for the feedback system.
 * Each theme specifies a unique id, display name, description, whether it
 * uses Lottie animations, and the ordered array of rating objects (score 5 → 1).
 *
 * Steps to add a new Rating theme:
 *  1. Import any required assets at the top of this file.
 *  2. Add a new entry to RATING_THEMES using the same shape as existing ones.
 *  3. You must add an entry into `vesta.platform\config-json\fbs-rating-themes.json` in Platform code
 *  4. The theme will automatically be available in the mobile web app for rendering, 
 *     and in Admin app via the API endpoint GET /fbs/rating-themes.
 */

/* --- Static emoji assets (default theme) --- */
import img5 from "./../assets/fbs/default.5.png";
import img41 from "./../assets/fbs/default.4.1.png";
import img3 from "./../assets/fbs/default.3.png";
import img31 from "./../assets/fbs/default.3.1.png";
import img2 from "./../assets/fbs/default.2.png";

/* --- Lottie animation assets (animated emojis theme) --- */
import anim1 from "./../assets/fbs/animated-ratings/animated.1.json";
import anim2 from "./../assets/fbs/animated-ratings/animated.2.json";
import anim3 from "./../assets/fbs/animated-ratings/animated.3.json";
import anim4 from "./../assets/fbs/animated-ratings/animated.4.json";
import anim5 from "./../assets/fbs/animated-ratings/animated.5.json";

/* --- Static SVG assets (gradient-simple theme) --- */
import gradientSimple1 from "./../assets/fbs/ratings/gradient-simple/rating-1.svg";
import gradientSimple2 from "./../assets/fbs/ratings/gradient-simple/rating-2.svg";
import gradientSimple3 from "./../assets/fbs/ratings/gradient-simple/rating-3.svg";
import gradientSimple4 from "./../assets/fbs/ratings/gradient-simple/rating-4.svg";
import gradientSimple5 from "./../assets/fbs/ratings/gradient-simple/rating-5.svg";

// ---------------------------------------------------------------------------
// Theme definitions
// ---------------------------------------------------------------------------

export const RATING_THEMES = {
    /**
     * emojis — classic static emoji images (default)
     */
    emojis: {
        id: "emojis",
        name: "Emojis",
        description: "Classic static emoji face ratings",
        animated: false,
        ratings: [
            {
                score: 5,
                img: img5,
                name: "Excellent",
                text: "Thank you for your feedback",
            },
            {
                score: 4,
                img: img41,
                name: "Very Good",
                text: "Thank you for your feedback",
            },
            {
                score: 3,
                img: img3,
                name: "Good",
                text: "Where can we improve?",
            },
            {
                score: 2,
                img: img31,
                name: "Fair",
                text: "How can we make things right?",
            },
            {
                score: 1,
                img: img2,
                name: "Poor",
                text: "How can we make things right?",
            },
        ],
    },

    /**
     * animatedEmojis — Lottie-animated emoji faces
     */
    animatedEmojis: {
        id: "animatedEmojis",
        name: "Animated Emojis",
        description: "Lively animated emoji face ratings",
        animated: true,
        ratings: [
            {
                score: 5,
                img: anim5,
                name: "Excellent",
                text: "Thank you for your feedback",
            },
            {
                score: 4,
                img: anim4,
                name: "Very Good",
                text: "Thank you for your feedback",
            },
            {
                score: 3,
                img: anim3,
                name: "Good",
                text: "Where can we improve?",
            },
            {
                score: 2,
                img: anim2,
                name: "Fair",
                text: "How can we make things right?",
            },
            {
                score: 1,
                img: anim1,
                name: "Poor",
                text: "How can we make things right?",
            },
        ],
    },

    /**
     * gradient-simple — simple gradient SVG ratings
     */
    "gradient-simple": {
        id: "gradient-simple",
        name: "Gradient Simple",
        description: "Simple gradient-based SVG face ratings",
        animated: false,
        ratings: [
            {
                score: 5,
                img: gradientSimple5,
                name: "Excellent",
                text: "Thank you for your feedback",
            },
            {
                score: 4,
                img: gradientSimple4,
                name: "Very Good",
                text: "Thank you for your feedback",
            },
            {
                score: 3,
                img: gradientSimple3,
                name: "Good",
                text: "Where can we improve?",
            },
            {
                score: 2,
                img: gradientSimple2,
                name: "Fair",
                text: "How can we make things right?",
            },
            {
                score: 1,
                img: gradientSimple1,
                name: "Poor",
                text: "How can we make things right?",
            },
        ],
    },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** The global theme used when no ratingTheme is configured on the template. */
export const DEFAULT_RATING_THEME_ID = "gradient-simple";

/**
 * Serialisable metadata list (no image assets).
 * Mirrors exactly what the platform API returns from GET /fbs/rating-themes.
 */
export const RATING_THEMES_META = Object.values(RATING_THEMES).map(
    ({ id, name, description, animated }) => ({
        id,
        name,
        description,
        animated,
    }),
);

/**
 * Returns the theme object for the given id.
 * Falls back to the default theme if the id is unknown or falsy.
 */
export function getRatingTheme(themeId) {
    return RATING_THEMES[themeId] ?? RATING_THEMES[DEFAULT_RATING_THEME_ID];
}
